import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, Terminal, CheckCircle2 } from 'lucide-react';
import { getGeminiResponse } from '@/services/gemini';

const NexusAssistant = ({ isOpen, onClose, activeModuleId, theme = 'dark' }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);
  const isDark = theme === 'dark';

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);
    const response = await getGeminiResponse(input, activeModuleId || 'General');
    setMessages((prev) => [...prev, { role: 'nexus', content: response.message, orchestration: response.orchestration }]);
    setIsTyping(false);
  };

  if (!isOpen) return null;

  return (
    <div data-testid="nexus-assistant" className={`fixed right-4 top-14 bottom-14 w-96 border rounded-xl shadow-2xl flex flex-col z-[150] overflow-hidden ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-white border-slate-200 shadow-xl'}`}>
      <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-[#333] bg-[#151515]' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white"><Sparkles size={18} /></div>
          <div>
            <h3 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Nexus Assistant</h3>
            <p className="text-[10px] text-green-500 font-mono uppercase tracking-widest">Active Orchestrator</p>
          </div>
        </div>
        <button data-testid="nexus-close-btn" onClick={onClose} className={`${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}><X size={18} /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-10 space-y-4">
            <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center text-blue-500 ${isDark ? 'bg-[#222]' : 'bg-blue-50'}`}><Sparkles size={32} /></div>
            <div className="space-y-1">
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>How can I assist you today?</p>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>Query modules, audit faults, or propose actions.</p>
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg p-3 text-sm ${msg.role === 'user' ? 'bg-blue-600 text-white ml-8' : isDark ? 'bg-[#252525] text-gray-200 border border-[#333] mr-8' : 'bg-slate-100 text-slate-800 border border-slate-200 mr-8'}`}>
              {msg.content}
              {msg.orchestration && (
                <div className={`mt-3 p-2 rounded border ${isDark ? 'bg-black/40 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                  <div className="flex items-center gap-2 text-blue-400 mb-2"><Terminal size={14} /><span className="text-[10px] uppercase font-bold tracking-widest">Proposed Action</span></div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs"><span className={isDark ? 'text-gray-500' : 'text-slate-500'}>Operation:</span><span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{msg.orchestration.action}</span></div>
                    <div className="flex justify-between text-xs"><span className={isDark ? 'text-gray-500' : 'text-slate-500'}>Target:</span><span className={`font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>{msg.orchestration.target}</span></div>
                    <button data-testid="nexus-confirm-btn" className="w-full mt-2 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs font-bold transition-colors flex items-center justify-center gap-2"><CheckCircle2 size={14} />Confirm Transaction</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className={`rounded-lg p-3 animate-pulse ${isDark ? 'bg-[#252525]' : 'bg-slate-100'}`}>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-600 animate-bounce"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-gray-600 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-1.5 h-1.5 rounded-full bg-gray-600 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={`p-4 border-t ${isDark ? 'border-[#333] bg-[#151515]' : 'border-slate-200 bg-slate-50'}`}>
        <div className="relative">
          <input
            data-testid="nexus-input"
            type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask Nexus..."
            className={`w-full border rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${isDark ? 'bg-[#222] border-[#333] text-white' : 'bg-white border-slate-200 text-slate-900'}`}
          />
          <button data-testid="nexus-send-btn" onClick={handleSend} disabled={!input.trim()} className={`absolute right-2 top-2 disabled:opacity-50 ${isDark ? 'text-gray-500 hover:text-blue-500' : 'text-slate-400 hover:text-blue-500'}`}><Send size={18} /></button>
        </div>
      </div>
    </div>
  );
};

export default NexusAssistant;
